import {DurationHHMMSS} from "../../../../helper/dateTime";
import React, {ReactNode} from "react";
import ImageUnit from "../../ImageUnit/ImageUnit";
import ImageMisc, {IconType} from "../../ImageMisc/ImageMisc";
import Tribe from "../../../enum/Tribe";
import {BuildingEffect} from "../../../index";
import WithTooltip from "../../../../react/components/ui/withTooltip";

const iconMap = {
    'healTime': 'healTime',
    'trainingTimeBarracks': 'infantryBonusTime',
    'trainingTimeStable': 'cavalryBonusTime',
    'trainingTimeWorkshop': 'siegeBonusTime',
    'trainingTimeResidence': 'residenceBonusTime',
    'merchants': 'merchants',
    'storageWarehouse': 'warehouseCap',
    'storageGranary': 'granaryCap',
    'storageCranny': 'crannyCap',
    'durability': 'stabilityBonus',
    'traps': 'maxTraps',
    'merchantBoost': 'merchantCap',
    'offBoost': 'attackBonus',
    'oasisBoost': 'oasisBonus',
    'ships': 'maxShips',
    'troopSpeedBoost': 'troopSpeed',
    'troopVisibility': 'troopVisibility',
    'defFlat': 'defenceFlat',
    'defBonus': 'defenceBonus',
    'production1': 'lumberBonus',
    'production2': 'clayBonus',
    'production3': 'ironBonus',
    'production4': 'cropBonus',
    'productionBoost1': 'lumberBonus',
    'productionBoost2': 'clayBonus',
    'productionBoost3': 'ironBonus',
    'productionBoost4': 'cropBonus',
    'buildingTime': 'constructionTime',
    'smallPartyTime': 'townhallSmallParty',
    'largePartyTime': 'townhallBigParty',
    'oases': 'oasis',
    'partyTime': "breweryParty",

} as Record<BuildingEffect, IconType>;

const translationKeyMap = {
    'trainingTimeBarracks': 'buildingEffect_trainingTime',
    'trainingTimeStable': 'buildingEffect_trainingTime',
    'trainingTimeWorkshop': 'buildingEffect_trainingTime',
    'production1': 'buildingEffect_production',
    'production2': 'buildingEffect_production',
    'production3': 'buildingEffect_production',
    'production4': 'buildingEffect_production',
    'merchantBoostRoman': 'buildingEffect_merchantBoost',
    'storageCrannyGaul': 'buildingEffect_storageCranny',
} as Record<BuildingEffect, string>

// NO TRANSLATION YET: header_sum, req_CapitalOrCity, healTime, offBoost, defFlat, smallPartyTime, largePartyTime, partyTime, slots

const formatEffect = (effect: BuildingEffect, value: any): string|ReactNode => {
    if (value === null) {
        return null;
    }
    switch (effect) {
        case 'oasisBoost':
        case 'durability':
        case 'offBoost':
        case 'troopSpeedBoost':
            return '+' + (value as number).toLocaleString(undefined, { style: 'percent' });
        case 'defFlat':
            return (value as number).toLocaleString();
        case 'healTime':
        case 'defBonus':
        case 'buildingTime':
            return (value as number).toLocaleString(undefined, {
                style: 'percent',
                maximumFractionDigits: 1,
                minimumFractionDigits: 1
            });
        case 'smallPartyTime':
        case 'largePartyTime':
        case 'partyTime':
            return DurationHHMMSS(Math.round(value as number));
    }

    switch (true) {
        case effect.startsWith('trainingTime'):
            return (value as number).toLocaleString(undefined, {
                style: 'percent',
                maximumFractionDigits: 1,
                minimumFractionDigits: 1
            });
        case effect.startsWith('storage'):
            return (value as number).toLocaleString();
        case effect.startsWith('productionBoost'):
        case effect.startsWith('merchantBoost'):
            return '+' + (value as number).toLocaleString(undefined, { style: 'percent' });
        case effect.startsWith('production'):
            return (value as number).toLocaleString();
    }

    return value?.toString() ?? '';
}

export const formatEffectWithIcon = (effect: BuildingEffect, value: any, tooltip: ReactNode) => {
    const formattedEffect = formatEffect(effect, value);

    return <>
        {formattedEffect}
        {formattedEffect !== null && <div className='valueIcon'><WithTooltip tooltip={tooltip}>{getEffectIcon(effect, false)}</WithTooltip></div>}
    </>
}

export const getEffectIcon = (effect: BuildingEffect, large: boolean): ReactNode|null => {
    if (iconMap[effect] !== undefined) {
        return <ImageMisc type={iconMap[effect]} size={large ? 24 : 16} />;
    }

    switch (effect) {
        case 'slots':
            return <>
                <ImageUnit tribe={Tribe.Roman} troopId={9} size={16} />
                { large && <ImageUnit tribe={Tribe.Roman} troopId={10} size={16} /> }
            </>
        case 'merchantBoostRoman':
            return <>
                { large && <ImageMisc type='merchantCap' size={24} /> }
                <ImageMisc type='roman' size={large ? 24 : 16} />
            </>
        case 'storageCrannyGaul':
            return <>
                { large && <ImageMisc type='crannyCap' size={24} /> }
                <ImageMisc type='gaul' size={large ? 24 : 16} />
            </>
    }

    return null;
}

export const getEffectTranslationKey = (effect: BuildingEffect): string => {
    return translationKeyMap[effect] ?? 'buildingEffect_' + effect;
}
